import * as React from 'react';
import { toast } from 'sonner';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { DataTable } from '../../../design-system/components/DataTable';
import { EmptyState } from '../../../design-system/components/EmptyState';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { NoAccessState } from '../../../design-system/components/NoAccessState';
import { useApi } from '../../../design-system/useApi';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { VISUAL, statusPill, statusDot } from '../../../design-system/visual';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';
import { Plus, Receipt } from 'lucide-react';
import { hrApi } from '../api';
import type { PenaltyRule, PenaltyRuleType } from '../types';
import { AttendanceSettingsPanel } from '../components/AttendanceSettingsPanel';

const RULE_TYPES: PenaltyRuleType[] = ['late_arrival', 'early_departure', 'absent_day', 'unapproved_leave'];

export function PenaltyRulesPage() {
  const confirm = useConfirm();
  const [activeOnly, setActiveOnly] = React.useState(false);
  const { data, loading, error, status, reload } = useApi<{ rules: PenaltyRule[] }>(
    `/api/hr/penalty-rules${activeOnly ? '?active_only=1' : ''}`,
    [activeOnly],
  );
  const [draft, setDraft] = React.useState<{ name: string; rule_type: PenaltyRuleType; threshold_minutes: string; amount: string }>({
    name: '', rule_type: 'late_arrival', threshold_minutes: '15', amount: '',
  });
  const [saving, setSaving] = React.useState(false);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.name.trim() || !draft.amount) { toast.error('Name and amount are required'); return; }
    setSaving(true);
    try {
      await hrApi.createPenaltyRule({
        name: draft.name.trim(),
        rule_type: draft.rule_type,
        threshold_minutes: draft.threshold_minutes ? Number(draft.threshold_minutes) : null,
        amount: Number(draft.amount),
      });
      toast.success('Rule added');
      setDraft({ name: '', rule_type: 'late_arrival', threshold_minutes: '15', amount: '' });
      reload();
    } catch (err: any) { toast.error('Could not add', { description: err.message }); }
    finally { setSaving(false); }
  }

  async function toggle(rule: PenaltyRule) {
    try {
      await hrApi.updatePenaltyRule(rule.id, { is_active: !rule.is_active });
      reload();
    } catch (err: any) { toast.error('Could not toggle', { description: err.message }); }
  }

  async function del(id: string) {
    if (!(await confirm({ title: 'Delete this rule?', destructive: true, confirmLabel: 'Delete' }))) return;
    try {
      await hrApi.deletePenaltyRule(id);
      toast.success('Deleted');
      reload();
    } catch (err: any) { toast.error('Delete failed', { description: err.message }); }
  }

  return (
    <div>
      <PageHeader
        eyebrow="Team / Settings"
        title="Attendance & penalties"
        description="Deductions the Payroll module applies automatically, plus the attendance policy that governs idle auto-pause and breaks."
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 max-w-4xl">
        <Tabs defaultValue="rules">
          <TabsList className="mb-6">
            <TabsTrigger value="rules">Penalty rules</TabsTrigger>
            <TabsTrigger value="attendance">Attendance policy</TabsTrigger>
          </TabsList>

          <TabsContent value="attendance">
            <AttendanceSettingsPanel />
          </TabsContent>

          <TabsContent value="rules" className="space-y-6">
        {/* Rules are manager-only on the server — a 403 here previously fell
            through to the friendly "No rules yet" empty state. Be honest, and
            hide the create form too (its POST would 403 the same way). */}
        {status === 403 ? (
          <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <NoAccessState description="Penalty rules are managed by workspace owners and HR managers." />
          </section>
        ) : error ? (
          <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <ErrorState title="Couldn't load penalty rules" description={error} onRetry={reload} />
          </section>
        ) : (
        <>
        <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
          <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>New rule</h3>
          <form onSubmit={create} className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="grid gap-1.5 sm:col-span-2">
              <Label>Name</Label>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Late arrival > 15 minutes" />
            </div>
            <div className="grid gap-1.5">
              <Label>Type</Label>
              <Select value={draft.rule_type} onValueChange={(v) => setDraft({ ...draft, rule_type: v as PenaltyRuleType })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RULE_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Threshold (min)</Label>
              <Input type="number" min={0} value={draft.threshold_minutes} onChange={(e) => setDraft({ ...draft, threshold_minutes: e.target.value })} />
            </div>
            <div className="grid gap-1.5 sm:col-span-3">
              <Label>Amount</Label>
              <Input type="number" min={0} step="0.01" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} placeholder="100.00" />
            </div>
            <div className="sm:col-span-1 flex items-end justify-end">
              <Button type="submit" disabled={saving} className="rounded-full h-10 px-5 text-[13px] font-semibold gap-1.5">
                <Plus className="h-3.5 w-3.5" />
                {saving ? 'Saving…' : 'Add rule'}
              </Button>
            </div>
          </form>
        </section>

        <div className="flex items-center justify-between">
          <h3 className={VISUAL.dashSection}>Rules</h3>
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-muted/50 ring-1 ring-border/40">
            <button
              type="button"
              onClick={() => setActiveOnly(false)}
              className={[
                'inline-flex items-center px-3.5 h-8 text-[12px] font-semibold rounded-full transition-colors',
                !activeOnly ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground',
              ].join(' ')}
            >All</button>
            <button
              type="button"
              onClick={() => setActiveOnly(true)}
              className={[
                'inline-flex items-center px-3.5 h-8 text-[12px] font-semibold rounded-full transition-colors',
                activeOnly ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground',
              ].join(' ')}
            >Active only</button>
          </div>
        </div>

        <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
          <DataTable<PenaltyRule>
            loading={loading}
            rows={data?.rules ?? []}
            rowKey={(r) => r.id}
            columns={[
              { key: 'name', header: 'Name', cell: (r) => <span className="font-medium text-foreground">{r.name}</span> },
              { key: 'type', header: 'Type', cell: (r) => <span className="font-mono text-[12px] uppercase tracking-wide text-muted-foreground">{r.rule_type.replace('_', ' ')}</span> },
              { key: 'threshold', header: 'Threshold', cell: (r) => r.threshold_minutes ? `${r.threshold_minutes} min` : '—' },
              { key: 'amount', header: 'Amount', cell: (r) => <span className="font-mono tabular-nums">{r.amount}</span> },
              { key: 'active', header: 'Active', cell: (r) => r.is_active
                ? <span className={statusPill('success')}><span className={statusDot('success')} />Active</span>
                : <span className={statusPill('muted')}><span className={statusDot('muted')} />Inactive</span> },
              { key: 'actions', header: '', cell: (r) => (
                <div className="flex gap-1 justify-end">
                  <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={() => toggle(r)}>{r.is_active ? 'Disable' : 'Enable'}</Button>
                  <Button size="sm" variant="ghost" className="rounded-full h-8 text-muted-foreground hover:text-destructive" onClick={() => del(r.id)}>Delete</Button>
                </div>
              ) },
            ]}
            emptyState={<EmptyState icon={<Receipt />} title="No rules yet" description="Add a rule above. The Payroll module reads these during every run." />}
          />
        </section>
        </>
        )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
